import React, { useState } from 'react'
import { router, usePage } from '@inertiajs/react'
import { ShieldCheck, Send, BadgeCheck, AlertTriangle, FileText, KeyRound } from 'lucide-react'
import { cn } from '../lib/utils'

// The patient's signed intake pack as ONE card — one signature, one identity state, one copy
// state, one integrity verdict, one PDF. Replaces SignedFormRow (three identical rows with three
// identical button pairs for the three forms of one sitting — audit 2026-09-03).
//
// 🚨 SHARED BY BOTH PAGES ON PURPOSE — the Digital file AND the patient page's Visits & Chats tab.
// Server side is likewise one thing: SignedIntakePack#props.
//
// Two facts, kept apart on screen exactly as they are in the database (S-3 #6):
//   • Code confirmed — the signer proved they hold the WhatsApp/email on file. NOT identity.
//   • ID checked    — a NAMED member of staff saw the person and their ID (A-7). Only this
//                     silences the "check ID on arrival" state. The server refuses "Reception",
//                     the patient, or a code as the verifier, so the card asks for a name.

const COPY_CHOICES = [
  { value: 'whatsapp',  label: 'WhatsApp' },
  { value: 'email',     label: 'Email' },
  { value: 'printed',   label: 'Printed' },
  { value: 'collected', label: 'Collected' },
]

// A shared login is not a person — leave the box empty so the desk types who actually looked.
const SHARED_LOGINS = /^(reception|surgery\s*\d+.*|desk|staff)$/i

function Fact({ label, ok, children, warn = false }) {
  return (
    <div className="flex gap-2">
      <dt className="w-24 shrink-0 text-brand-muted">{label}</dt>
      <dd className={cn(ok ? 'text-brand-ink' : warn ? 'font-semibold text-amber-700' : 'text-brand-muted')}>{children}</dd>
    </div>
  )
}

export default function SignedPackCard({ pack, compact = false }) {
  const { current_user: currentUser } = usePage().props
  const [mode, setMode] = useState(null) // 'identity' | 'copy' | null
  const defaultName = currentUser?.name && !SHARED_LOGINS.test(currentUser.name) ? currentUser.name : ''
  const [checkedBy, setCheckedBy] = useState(defaultName)
  const [busy, setBusy] = useState(false)

  if (!pack) return null

  const post = (path, payload) => {
    setBusy(true)
    router.post(path, payload, {
      preserveScroll: true,
      onFinish: () => { setBusy(false); setMode(null) },
    })
  }
  const submitIdentity = (e) => {
    e?.preventDefault()
    if (checkedBy.trim().length < 2) return
    post(`/patients/${pack.patient_id}/signed-pack/verify-identity`, { verified_by: checkedBy.trim(), identity_method: 'in_person_id' })
  }

  const needsIdCheck = !pack.identity_verified
  const needsCopy    = !pack.copy_given
  const altered      = pack.integrity === 'altered'

  return (
    <div className={cn('rounded-xl border bg-white', altered ? 'border-red-300' : needsIdCheck ? 'border-amber-300' : 'border-brand-border')}>
      <div className="flex flex-wrap items-center gap-2 border-b border-brand-border/60 px-4 py-2.5">
        <ShieldCheck size={14} className={altered ? 'text-red-600' : 'text-emerald-600'} />
        <span className="text-sm font-semibold text-brand-ink">Signed intake pack</span>
        <span className="text-[11px] text-brand-muted">
          {pack.forms.map((f) => f.name).join(' · ')}
        </span>
        <a href={pack.pdf_url} target="_blank" rel="noopener noreferrer"
          className="ml-auto inline-flex items-center gap-1 rounded-md border border-brand-border bg-white px-2 py-1 text-[11px] font-semibold text-brand-ink hover:bg-brand-surface">
          <FileText size={11} /> Open PDF
        </a>
      </div>

      <div className="px-4 py-3">
        {altered && (
          <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-red-700">
            <AlertTriangle size={13} /> This record has CHANGED since it was signed — do not rely on it. {pack.integrity_note}
          </p>
        )}

        <dl className="space-y-1 text-[12px]">
          <Fact label="Signed" ok>
            {pack.signed_at} by <span className="font-semibold">{pack.signed_name}</span>
            {pack.signing_as ? ` (${pack.signing_as})` : ''}
            {pack.co_signer_name ? ` · co-signed by ${pack.co_signer_name}` : ''}
          </Fact>
          <Fact label="Code" ok={pack.authenticated}>
            {pack.authenticated
              ? <><KeyRound size={11} className="mr-1 inline text-brand-muted" />{pack.authentication_label} · {pack.authenticated_at}</>
              : 'no code was sent — signed without a code'}
          </Fact>
          <Fact label="ID" ok={pack.identity_verified} warn={needsIdCheck}>
            {pack.identity_verified
              ? `${pack.identity_label} · ${pack.identity_verified_at}`
              : 'not checked yet — check their ID when they arrive'}
          </Fact>
          <Fact label="Their copy" ok={pack.copy_given}>
            {pack.copy_given ? `given ${pack.copy_given_at} · ${pack.copy_given_method}` : 'not given yet'}
          </Fact>
          {!altered && pack.integrity !== 'verified' && (
            <Fact label="Integrity" ok={false}>{pack.integrity_note}</Fact>
          )}
          {pack.earlier_signed_count > 0 && !compact && (
            <Fact label="History" ok>{pack.earlier_signed_count} earlier signed {pack.earlier_signed_count === 1 ? 'version' : 'versions'} kept on file</Fact>
          )}
        </dl>

        {mode === 'identity' && (
          <form onSubmit={submitIdentity} className="mt-3 rounded-lg border border-brand-border bg-brand-surface/60 p-2.5">
            <p className="mb-1.5 text-[11px] font-semibold text-brand-ink">Who checked the patient's ID at the desk?</p>
            <div className="flex flex-wrap items-center gap-1.5">
              <input
                autoFocus
                value={checkedBy}
                onChange={(e) => setCheckedBy(e.target.value)}
                placeholder="Your name"
                className="w-44 rounded-md border border-brand-border bg-white px-2 py-1 text-[12px] text-brand-ink focus:border-brand-primary focus:outline-none"
              />
              <button type="submit" disabled={busy || checkedBy.trim().length < 2}
                className="inline-flex items-center gap-1 rounded-md bg-brand-primary px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-brand-primary-dark disabled:opacity-50">
                <BadgeCheck size={11} /> Confirm ID checked
              </button>
              <button type="button" onClick={() => setMode(null)} className="px-2 py-1 text-[11px] text-brand-muted hover:text-brand-ink">Cancel</button>
            </div>
            <p className="mt-1.5 text-[10.5px] text-brand-muted">
              Recorded against all {pack.forms.length} forms as one check. The signature itself is never changed.
            </p>
          </form>
        )}

        {mode === 'copy' && (
          <div className="mt-3 rounded-lg border border-brand-border bg-brand-surface/60 p-2.5">
            <p className="mb-1.5 text-[11px] font-semibold text-brand-ink">How did the patient get their copy?</p>
            <div className="flex flex-wrap gap-1.5">
              {COPY_CHOICES.map((c) => (
                <button key={c.value} disabled={busy}
                  onClick={() => post(`/patients/${pack.patient_id}/signed-pack/copy-given`, { copy_method: c.value })}
                  className="rounded-md border border-brand-border bg-white px-2 py-1 text-[11px] font-semibold text-brand-ink hover:border-brand-primary hover:text-brand-primary disabled:opacity-50">
                  {c.label}
                </button>
              ))}
              <button onClick={() => setMode(null)} className="px-2 py-1 text-[11px] text-brand-muted hover:text-brand-ink">Cancel</button>
            </div>
          </div>
        )}

        {!mode && (needsIdCheck || needsCopy) && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {needsIdCheck && (
              <button onClick={() => setMode('identity')}
                className="inline-flex items-center gap-1 rounded-md border border-amber-500 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-800 hover:bg-amber-100">
                <BadgeCheck size={11} /> Confirm ID checked
              </button>
            )}
            {needsCopy && (
              <button onClick={() => setMode('copy')}
                className="inline-flex items-center gap-1 rounded-md border border-brand-border bg-white px-2.5 py-1 text-[11px] font-semibold text-brand-ink hover:bg-brand-surface">
                <Send size={11} /> Record copy given
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
